import React from 'react';
import { Row, Col, Input, Button, Space, Select } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { SEARCH_MATCH_MODE_OPTIONS, DEVICE_LOCATION_SEARCH_SCOPE_OPTIONS } from '../utils/listSearch';

/**
 * 统一的列表工具栏组件
 * @param {Object} props
 * @param {String} props.searchPlaceholder - 搜索框占位符
 * @param {String} props.searchValue - 搜索框值
 * @param {Array} props.searchScopeOptions - 搜索范围 [{ value, label }]
 * @param {String} props.searchScope - 当前搜索范围
 * @param {Function} props.onSearchScopeChange - 搜索范围变化回调
 * @param {Boolean} props.showMatchMode - 是否显示模糊/精确切换
 * @param {String} props.matchMode - 当前匹配模式 fuzzy | exact
 * @param {Function} props.onMatchModeChange - 匹配模式变化回调
 * @param {Boolean} props.showLocationSearch - 是否显示位置二次筛选
 * @param {String} props.locationPlaceholder - 位置筛选占位符
 * @param {String} props.locationValue - 位置筛选值
 * @param {String} props.locationScope - 当前位置筛选范围
 * @param {Function} props.onLocationScopeChange - 位置范围变化回调
 * @param {String} props.locationMatchMode - 位置匹配模式 fuzzy | exact
 * @param {Function} props.onLocationMatchModeChange - 位置匹配模式变化回调
 * @param {Function} props.onLocationChange - 位置筛选框变化回调
 * @param {Function} props.onSearchChange - 搜索框变化回调
 * @param {Function} props.onSearch - 搜索回调
 * @param {Function} props.onReset - 重置回调
 * @param {Function} props.onRefresh - 刷新回调
 * @param {Function} props.onExport - 导出回调
 * @param {Array} props.filters - 筛选器配置 [{key, label, options, value, onChange}]
 * @param {React.ReactNode} props.beforeFilters - 渲染在筛选器左侧的自定义内容
 * @param {Array} props.actions - 操作按钮配置 [{key, label, icon, onClick, type, danger, disabled}]
 * @param {Boolean} props.showBatchDelete - 是否显示批量删除
 * @param {Number} props.selectedCount - 选中数量
 * @param {Function} props.onBatchDelete - 批量删除回调
 * @param {Boolean} props.showAdd - 是否显示新增按钮
 * @param {Function} props.onAdd - 新增回调
 */
const ListToolbar = ({
  searchPlaceholder = '请输入关键词搜索',
  searchValue = '',
  searchScopeOptions = [],
  searchScope,
  onSearchScopeChange,
  showMatchMode = false,
  matchMode = 'fuzzy',
  onMatchModeChange,
  showLocationSearch = false,
  locationPlaceholder = '筛选绑定房间或所属房源',
  locationValue = '',
  locationScope = 'all',
  onLocationScopeChange,
  locationMatchMode = 'fuzzy',
  onLocationMatchModeChange,
  onLocationChange,
  onSearchChange,
  onSearch,
  onReset,
  onRefresh,
  onExport,
  filters = [],
  beforeFilters = null,
  actions = [],
  showBatchDelete = false,
  selectedCount = 0,
  onBatchDelete,
  showAdd = false,
  onAdd,
}) => {
  // 导出数据功能
  const handleExport = () => {
    if (onExport) {
      onExport();
    } else {
      // 默认导出功能
      message.info('导出功能开发中...');
    }
  };

  // 处理搜索
  const handleSearch = () => {
    if (onSearch) {
      onSearch();
    }
  };

  // 处理重置
  const handleReset = () => {
    if (onReset) {
      onReset();
    }
  };

  // 处理刷新
  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  const searchInputStyle = { flex: 1, fontSize: 11, height: 26 };
  const matchSelectStyle = { width: 72, fontSize: 11 };
  const scopeSelectStyle = { width: 118, fontSize: 11 };
  const searchRowMaxWidth = (searchScopeOptions.length > 0 || showMatchMode) ? 820 : 600;
  const searchIcon = <SearchOutlined style={{ color: '#999' }} />;

  const renderSearchActions = (placeholder = false) => (
    <div style={placeholder ? { visibility: 'hidden', flexShrink: 0 } : { flexShrink: 0 }}>
      <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} size="small" tabIndex={placeholder ? -1 : 0}>
        搜索
      </Button>
      <Button onClick={handleReset} size="small" tabIndex={placeholder ? -1 : 0}>
        重置
      </Button>
    </div>
  );

  return (
    <div style={{ 
      marginBottom: 16, 
      background: '#ffffff', 
      padding: '14px 18px', 
      borderRadius: 10, 
      border: '1px solid rgba(51, 102, 255, 0.12)',
      boxShadow: '0 2px 8px rgba(51, 102, 255, 0.08)',
    }}>
      <Row gutter={[12, 8]} align="middle">
        {/* 左侧：搜索和筛选 */}
        <Col flex="auto">
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <div style={{ display: 'flex', width: '100%', maxWidth: searchRowMaxWidth }}>
              <Space.Compact style={{ flex: 1, minWidth: 0 }}>
                <Input
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                  onPressEnter={handleSearch}
                  allowClear
                  prefix={searchIcon}
                  style={searchInputStyle}
                />
                {showMatchMode && (
                  <Select
                    value={matchMode}
                    onChange={onMatchModeChange}
                    options={SEARCH_MATCH_MODE_OPTIONS}
                    style={matchSelectStyle}
                    size="small"
                  />
                )}
                {searchScopeOptions.length > 0 && (
                  <Select
                    value={searchScope}
                    onChange={onSearchScopeChange}
                    options={searchScopeOptions}
                    style={scopeSelectStyle}
                    size="small"
                  />
                )}
              </Space.Compact>
              {renderSearchActions()}
            </div>
            {showLocationSearch && (
              <div style={{ display: 'flex', width: '100%', maxWidth: searchRowMaxWidth }}>
                <Space.Compact style={{ flex: 1, minWidth: 0 }}>
                  <Input
                    placeholder={locationPlaceholder}
                    value={locationValue}
                    onChange={(e) => onLocationChange && onLocationChange(e.target.value)}
                    onPressEnter={handleSearch}
                    allowClear
                    prefix={searchIcon}
                    style={searchInputStyle}
                  />
                  <Select
                    value={locationMatchMode}
                    onChange={onLocationMatchModeChange}
                    options={SEARCH_MATCH_MODE_OPTIONS}
                    style={matchSelectStyle}
                    size="small"
                  />
                  <Select
                    value={locationScope}
                    onChange={onLocationScopeChange}
                    options={DEVICE_LOCATION_SEARCH_SCOPE_OPTIONS}
                    style={scopeSelectStyle}
                    size="small"
                  />
                </Space.Compact>
                {renderSearchActions(true)}
              </div>
            )}
          </Space>
        </Col>

        {/* 中间：筛选器 */}
        {(beforeFilters || filters.length > 0) && (
          <Col>
            <Space>
              {beforeFilters}
              {filters.map(filter => (
                <Select
                  key={filter.key}
                  placeholder={filter.label}
                  value={filter.value}
                  onChange={filter.onChange}
                  options={filter.options}
                  style={{ minWidth: 110, fontSize: 11 }}
                  allowClear
                  size="small"
                />
              ))}
            </Space>
          </Col>
        )}

        {/* 右侧：操作按钮 */}
        <Col>
          <Space size="small">
            {/* 刷新按钮 */}
            {onRefresh && (
              <Button 
                icon={<ReloadOutlined />} 
                onClick={handleRefresh}
                size="small"
              >
                刷新
              </Button>
            )}

            {/* 导出按钮 */}
            {onExport && (
              <Button 
                icon={<DownloadOutlined />} 
                type="primary"
                onClick={handleExport}
                size="small"
              >
                导出数据
              </Button>
            )}

            {/* 批量删除 */}
            {showBatchDelete && (
              <Button 
                icon={<DeleteOutlined />} 
                type="primary"
                danger
                onClick={onBatchDelete}
                disabled={selectedCount === 0}
                size="small"
              >
                批量删除 {selectedCount > 0 ? `(${selectedCount})` : ''}
              </Button>
            )}

            {/* 新增按钮 */}
            {showAdd && (
              <Button 
                icon={<PlusOutlined />} 
                type="primary"
                onClick={onAdd}
                size="small"
              >
                新增
              </Button>
            )}

            {/* 自定义操作按钮 */}
            {actions.map(action => (
              <Button
                key={action.key}
                icon={action.icon}
                type={action.type || 'default'}
                danger={action.danger}
                onClick={action.onClick}
                disabled={action.disabled}
                size="small"
              >
                {action.label}
              </Button>
            ))}
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default ListToolbar;

